import { logger } from '../utils/logger';

// Use relative path in dev mode (via Vite proxy) or absolute URL in production
// Remove trailing /api if present to avoid double /api in URLs
let API_BASE_URL = import.meta.env.VITE_API_BASE_URL || (import.meta.env.DEV ? '' : '/api');
// Remove trailing /api to avoid double /api (endpoints already include /api)
if (API_BASE_URL.endsWith('/api')) {
  API_BASE_URL = API_BASE_URL.slice(0, -4);
}

class ApiService {
  private baseUrl: string;
  private csrfToken: string | null = null;
  private authToken: string | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    // Remove trailing /api if present
    if (baseUrl.endsWith('/api')) {
      baseUrl = baseUrl.slice(0, -4);
    }
    this.baseUrl = baseUrl;
    this.initializeCsrfToken();
    this.loadAuthToken();
  }

  private loadAuthToken(): void {
    this.authToken = localStorage.getItem('authToken');
  }

  public setAuthToken(token: string | null): void {
    this.authToken = token;
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  private async initializeCsrfToken(): Promise<void> {
    try {
      // Fetch CSRF token from backend
      // Note: endpoint already includes /api, so we don't add it again
      const response = await fetch(`${this.baseUrl}/api/auth/csrf-token`, {
        method: 'GET',
        credentials: 'include', // Include cookies
      });
      
      if (response.ok) {
        const data = await response.json();
        this.csrfToken = data.csrfToken;
      }
    } catch (error) {
      // Silently handle connection errors - server might not be running yet
      // CSRF token will be fetched later when needed
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        // Connection refused or server not running - this is expected during initial load
        // Don't log to avoid console noise
        return;
      }
      // Only log unexpected errors in development
      if (import.meta.env.DEV) {
        logger.warn('Failed to fetch CSRF token:', error);
      }
    }
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include', // Include cookies for httpOnly token storage
      ...options,
    };

    // Add auth token if available
    if (this.authToken) {
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${this.authToken}`,
      };
    }
    
    // Add user ID from sessionStorage if available
    try {
      const userStr = sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user && user.id) {
          config.headers = {
            ...config.headers,
            'x-user-id': user.id,
          };
        }
      }
    } catch (_) {
      // ignore
    }

    // Add tenant header if available (tenant-scoped APIs require this)
    // Always send schoolCode if available, even for super-admin (they may be working with a specific tenant)
    // Super-admin APIs (/admin/*) will ignore this header, but tenant APIs require it
    try {
      const schoolCode = localStorage.getItem('schoolCode');
      if (schoolCode && String(schoolCode).trim()) {
        config.headers = {
          ...config.headers,
          'x-school-code': String(schoolCode).trim(),
        };
      }
    } catch {
      // ignore
    }

    // Add CSRF token for state-changing requests
    if (this.csrfToken && (options.method === 'POST' || options.method === 'PUT' || options.method === 'PATCH' || options.method === 'DELETE')) {
      config.headers = {
        ...config.headers,
        'X-CSRF-Token': this.csrfToken,
      };
    }

    try {
      // Only log in development mode
      logger.log('🌐 apiService.request() - Calling:', url);
      logger.log('🌐 apiService.request() - Headers:', {
        'Content-Type': config.headers?.['Content-Type'],
        'x-user-id': config.headers?.['x-user-id'],
        'Authorization': config.headers?.['Authorization'] ? 'Bearer ***' : 'none'
      });
      
      const response = await fetch(url, config);
      const contentType = response.headers.get('content-type');
      
      logger.log('🌐 apiService.request() - Response status:', response.status);
      logger.log('🌐 apiService.request() - Response headers:', {
        'content-type': contentType,
        'content-length': response.headers.get('content-length')
      });
      
      if (!response.ok) {
        // Try to extract error message from response body
        let errorMessage = `HTTP error! status: ${response.status}`;
        
        // Don't log 404 errors for logout endpoint (expected if endpoint doesn't exist)
        const isLogoutEndpoint = url.includes('/api/auth/logout');
        const shouldLogError = !(isLogoutEndpoint && response.status === 404);
        
        try {
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            // Backend returns { error: '...', message: '...' } or { message: '...' }
            // Use error field first (it contains formatted message), then message, then default
            errorMessage = errorData.error || errorData.message || errorMessage;
            // Only log detailed error in development mode
            // In production, user already sees toast notification, no need for console noise
            if (shouldLogError && import.meta.env.DEV) {
              logger.error('❌ apiService.request() - Error response:', errorData);
            }
          } else {
            const text = await response.text();
            if (text && text.trim()) {
              errorMessage = text;
            }
            // Only log detailed error in development mode
            if (shouldLogError && import.meta.env.DEV) {
              logger.error('❌ apiService.request() - Error text:', text);
            }
          }
        } catch {
          // If parsing fails, use default error message
        }
        
        throw new Error(errorMessage);
      }
      
      // If response is ok, parse JSON
      if (contentType && contentType.includes('application/json')) {
        const jsonData = await response.json();
        logger.log('✅ apiService.request() - Response data:', jsonData);
        return jsonData;
      } else {
        // For non-JSON responses, return text
        const text = await response.text();
        logger.log('✅ apiService.request() - Response text:', text);
        return text as T;
      }
    } catch (error) {
      // Don't log errors for logout endpoint 404s (expected if endpoint doesn't exist)
      const isLogoutEndpoint = url.includes('/api/auth/logout');
      if (!(isLogoutEndpoint && error instanceof Error && error.message.includes('404'))) {
        if (import.meta.env.DEV) {
          logger.error('❌ apiService.request() - Request failed:', error);
        }
      }
      throw error;
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    // Add cache-busting parameter to prevent browser caching
    const separator = endpoint.includes('?') ? '&' : '?';
    const cacheBuster = `_t=${Date.now()}`;
    const urlWithCacheBuster = `${endpoint}${separator}${cacheBuster}`;
    return this.request<T>(urlWithCacheBuster, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // Authentication methods
  async login(username: string, password: string, schoolCode?: string | null): Promise<{ success: boolean; user?: any; sessionId?: string; message?: string }> {
    // Ensure CSRF token is available before login
    if (!this.csrfToken) {
      await this.initializeCsrfToken();
    }
    
    const response = await this.request<{ success: boolean; user?: any; sessionId?: string; message?: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password, schoolCode: schoolCode || undefined }),
    });
    
    if (response.success && response.sessionId) {
      this.setAuthToken(response.sessionId);
    }
    
    return response;
  }

  async logout(): Promise<{ success: boolean }> {
    try {
      const response = await this.request<{ success: boolean }>('/api/auth/logout', {
        method: 'POST',
      });
      
      this.setAuthToken(null);
      return response;
    } catch (error: any) {
      // Silently handle 404 errors - endpoint might not exist on older server versions
      // Logout is primarily client-side anyway, so we can safely ignore API errors
      if (error?.message && error.message.includes('404')) {
        // Return success even if endpoint doesn't exist
        this.setAuthToken(null);
        return { success: true };
      }
      // Re-throw other errors
      throw error;
    }
  }

  async getCurrentUser(): Promise<{ success: boolean; user?: any; message?: string }> {
    return this.request<{ success: boolean; user?: any; message?: string }>('/api/auth/me');
  }
}

export const apiService = new ApiService();
export default apiService;
